/**
 * Departments module permissions.
 *
 * Capability = Admin, or the matching AspNetRight checked by CCMS.Api.DepartmentManagement
 * (Department, Designation, Ministry, Ministry.Role and so on; each covers GET, POST and PUT, and
 * the .Delete right covers DELETE). The "Departments Access" role opens the module and its read
 * surfaces; writes need the right itself.
 *
 * Member search (adding people) comes from MemberManagement and needs Member.Read.
 *
 * The UI only hides what the user cannot do; the API enforces the same rules.
 */

import { useCallback, useMemo } from 'react';
import { DEPARTMENT_RIGHTS, DEPARTMENT_ROLES } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '../useEnrichedAuth';

export { DEPARTMENT_RIGHTS, DEPARTMENT_ROLES };

/** Roles that open the Departments module (the domain gate). */
export const DEPARTMENTS_MODULE_ROLES: string[] = [DEPARTMENT_ROLES.Admin, DEPARTMENT_ROLES.DepartmentsAccess];

export function useDepartmentsPermissions() {
  const { user, hasRole, hasPermission } = useEnrichedAuth();

  const isAdmin = hasRole(DEPARTMENT_ROLES.Admin) || hasRole('admin');
  const isReader = hasRole(DEPARTMENT_ROLES.DepartmentsAccess);

  /** Admin, or (for read access) the "Departments Access" role, or the explicit right. */
  const can = useCallback(
    (right: string, readerDefault = false) => isAdmin || (readerDefault && isReader) || hasPermission(right),
    [isAdmin, isReader, hasPermission]
  );

  const perms = useMemo(() => {
    const R = DEPARTMENT_RIGHTS;
    const canViewDepartments = can(R.Department, true);
    const canManageDepartments = can(R.Department);
    const canViewDesignations = can(R.Designation, true) || canViewDepartments;
    const canManageDesignations = can(R.Designation);
    const canViewMinistries = can(R.Ministry, true);
    const canManageMinistries = can(R.Ministry);
    return {
      // Departments
      canViewDepartments,
      canManageDepartments,
      /** Deactivate a department (DELETE is a soft delete). */
      canDeleteDepartments: can(R.DepartmentDelete),
      /** Department membership (add, remove, change head) rides on the Department right. */
      canManageDepartmentMembers: canManageDepartments,
      canViewDepartmentMembers: canViewDepartments || can(R.MemberDepartment, true),
      // Designations
      canViewDesignations,
      canManageDesignations,
      canDeleteDesignations: can(R.DesignationDelete),
      canManageDesignationMembers: canManageDesignations || can(R.MemberDesignation),
      canManageDesignationTasks: canManageDesignations || can(R.DesignationTask),
      canDeleteDesignationTasks: can(R.DesignationTaskDelete),
      // Ministries
      canViewMinistries,
      canManageMinistries,
      canDeleteMinistries: can(R.MinistryDelete),
      canManageMinistryMembers: canManageMinistries,
      canViewMinistryRoles: can(R.MinistryRole, true) || canViewMinistries,
      canManageMinistryRoles: can(R.MinistryRole),
      canDeleteMinistryRoles: can(R.MinistryRoleDelete),
      canViewMinistryTeams: can(R.MinistryTeam, true) || canViewMinistries,
      canManageMinistryTeams: can(R.MinistryTeam),
      canDeleteMinistryTeams: can(R.MinistryTeamDelete),
      canViewMinistryEquipment: can(R.MinistryEquipment, true) || canViewMinistries,
      canManageMinistryEquipment: can(R.MinistryEquipment),
      canDeleteMinistryEquipment: can(R.MinistryEquipmentDelete),
      canViewMinistryActivities: can(R.MinistryActivity, true) || canViewMinistries,
      canManageMinistryActivities: can(R.MinistryActivity),
      canDeleteMinistryActivities: can(R.MinistryActivityDelete),
    };
  }, [can]);

  /** MemberManagement right needed to search for people to add. */
  const canSearchMembers = isAdmin || hasPermission('Member.Read');

  const canWriteAnything =
    perms.canManageDepartments ||
    perms.canManageDesignations ||
    perms.canManageMinistries ||
    perms.canManageMinistryRoles ||
    perms.canManageMinistryTeams ||
    perms.canManageMinistryEquipment ||
    perms.canManageMinistryActivities;

  return {
    ...perms,
    isAdmin,
    isReader,
    canSearchMembers,
    canWriteAnything,
    /** May open the module at all. */
    canOpenModule: perms.canViewDepartments || perms.canViewMinistries,
    userId: user?.id as string | undefined,
  };
}

export type DepartmentsPermissions = ReturnType<typeof useDepartmentsPermissions>;
